import {
  getEarliestStartDate,
  getXMinusTodaysDate,
  getYesterdaysDate,
} from '@helpers/dateUtils';
import { DateRangeMoment, DateRangeString, DATE_RANGE_TYPE } from './types';

interface ReducerState {
  selectedDateRangeTypeId: DATE_RANGE_TYPE;
  dateRangeInFilename: string;
  // Dates that will be sent to BE, rendered in Date range input field, and appended to filename
  selectedDateRange: DateRangeString;
  // The last custom start and end dates selected. To be used by DatePicker which requires Moment.js format
  lastSavedCustomDateRange: DateRangeMoment;
}

export enum ACTION_TYPES {
  SELECT_EXPORT_TYPE = 'select-export-type',
  SELECT_CUSTOM_DATES = 'select-custom-dates',
  SAVE_CUSTOM_DATES = 'save-custom-dates',
}

export const initialState = {
  selectedDateRangeTypeId: DATE_RANGE_TYPE.LAST_7_DAYS,
  dateRangeInFilename: `${getXMinusTodaysDate(8)} — ${getYesterdaysDate()}`,
  selectedDateRange: {
    startDate: getXMinusTodaysDate(8),
    endDate: getYesterdaysDate(),
  },
  lastSavedCustomDateRange: {
    startDate: null,
    endDate: null,
  },
};

export type Action = {
  type: ACTION_TYPES;
  payload: {
    dateRangeType: DATE_RANGE_TYPE;
    filename: string;
    // dateRange saved as strings, i.e '2023-01-01'
    dateRange?: DateRangeString;
    // customDate only interacts with the DateRangePicker component which requires Moment.js format
    customDate?: DateRangeMoment;
  };
};

const DATE_FORMAT = 'yyyy-MM-dd';

export const reducer = (state: ReducerState, action: Action) => {
  switch (action.type) {
    case ACTION_TYPES.SELECT_EXPORT_TYPE: {
      const dateRangeType = action.payload.dateRangeType;

      if (dateRangeType === DATE_RANGE_TYPE.LAST_7_DAYS) {
        return {
          ...state,
          selectedDateRangeTypeId: DATE_RANGE_TYPE.LAST_7_DAYS,
          selectedDateRange: {
            startDate: getXMinusTodaysDate(8),
            endDate: getYesterdaysDate(),
          },
          dateRangeInFilename: `${getXMinusTodaysDate(
            8,
          )} — ${getYesterdaysDate()}`,
        };
      }
      if (dateRangeType === DATE_RANGE_TYPE.LAST_28_DAYS) {
        return {
          ...state,
          selectedDateRangeTypeId: DATE_RANGE_TYPE.LAST_28_DAYS,
          selectedDateRange: {
            startDate: new Date(
              getXMinusTodaysDate(29, DATE_FORMAT),
            ).toISOString(),
            endDate: new Date(getYesterdaysDate(DATE_FORMAT)).toISOString(),
          },
          dateRangeInFilename: `${getXMinusTodaysDate(
            29,
          )} — ${getYesterdaysDate()}`,
        };
      }
      if (dateRangeType === DATE_RANGE_TYPE.LAST_90_DAYS) {
        return {
          ...state,
          selectedDateRangeTypeId: DATE_RANGE_TYPE.LAST_90_DAYS,
          selectedDateRange: {
            startDate: new Date(
              getXMinusTodaysDate(91, DATE_FORMAT),
            ).toISOString(),
            endDate: new Date(getYesterdaysDate(DATE_FORMAT)).toISOString(),
          },
          dateRangeInFilename: `${getXMinusTodaysDate(
            91,
          )} — ${getYesterdaysDate()}`,
        };
      }
      if (dateRangeType === DATE_RANGE_TYPE.CUSTOM_DATES) {
        return {
          ...state,
          selectedDateRangeTypeId: DATE_RANGE_TYPE.CUSTOM_DATES,
          dateRangeInFilename: '',
        };
      }
      if (dateRangeType === DATE_RANGE_TYPE.ALL_TIME) {
        return {
          ...state,
          selectedDateRangeTypeId: DATE_RANGE_TYPE.ALL_TIME,
          selectedDateRange: {
            startDate: new Date(
              getEarliestStartDate(DATE_FORMAT),
            ).toISOString(),
            endDate: new Date(getYesterdaysDate(DATE_FORMAT)).toISOString(),
          },
          dateRangeInFilename: action.payload.filename,
        };
      }
      return state;
    }
    case ACTION_TYPES.SELECT_CUSTOM_DATES: {
      return {
        ...state,
        selectedDateRangeTypeId: DATE_RANGE_TYPE.CUSTOM_DATES,
        selectedDateRange: {
          startDate: action.payload.dateRange?.startDate || '',
          endDate: action.payload.dateRange?.endDate || '',
        },
        dateRangeInFilename: action.payload.filename,
        lastSavedCustomDateRange: {
          startDate: action.payload.customDate?.startDate || null,
          endDate: action.payload.customDate?.endDate || null,
        },
      };
    }
    case ACTION_TYPES.SAVE_CUSTOM_DATES: {
      return {
        ...state,
        dateRangeInFilename: action.payload.filename,
        lastSavedCustomDateRange: {
          startDate: action.payload.customDate?.startDate || null,
          endDate: action.payload.customDate?.endDate || null,
        },
      };
    }
    default:
      return state;
  }
};
